import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Home as HomeIcon, RotateCcw, Award, ListChecks } from "lucide-react";
import { getOperation, getLevel, MEDAL_IMAGES, QUESTIONS_PER_ROUND } from "../lib/constants";
import { getMedal } from "../lib/medals";
import { PageShell } from "../components/PageShell";
import { ClayButton } from "../components/ClayButton";
import { Confetti } from "../components/Confetti";

export default function Results({ profile }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [showConfetti, setShowConfetti] = useState(false);

  const lastRound = location.state?.lastRound;
  const operationId = location.state?.operationId;
  const levelId = location.state?.levelId;
  const subOp = location.state?.subOp;
  const family = location.state?.family; // 'fracciones' | 'decimales' | undefined

  const buildLevelsPath = () => {
    if (family === "algebra") return `/secundaria/algebra/${subOp}`;
    if (family === "geometria") return `/secundaria/geometria/${subOp}`;
    return family ? `/practica/${family}/${subOp}` : `/practica/${operationId}`;
  };
  const buildReplayPath = () => {
    if (family === "algebra") return `/secundaria/algebra/${subOp}/${levelId}`;
    if (family === "geometria") return `/secundaria/geometria/${subOp}/${levelId}`;
    return family
      ? `/practica/${family}/${subOp}/${levelId}`
      : `/practica/${operationId}/${levelId}`;
  };

  useEffect(() => {
    if (!lastRound) {
      navigate("/dashboard", { replace: true });
      return;
    }
    if (lastRound.correct >= 7 || (lastRound.unlockedMedals && lastRound.unlockedMedals.length > 0)) {
      setShowConfetti(true);
      const t = setTimeout(() => setShowConfetti(false), 5000);
      return () => clearTimeout(t);
    }
  }, [lastRound, navigate]);

  if (!lastRound) return null;

  const op = getOperation(operationId);
  const level = getLevel(levelId);
  const total = lastRound.questions || QUESTIONS_PER_ROUND;
  const correct = lastRound.correct;
  const wrong = lastRound.wrong;
  const accuracy = Math.round((correct / total) * 100);
  const newlyUnlocked = lastRound.unlockedMedals || [];

  let emoji = "💪";
  let title = "¡Sigue practicando!";
  let titleColor = "#FF9500";
  if (accuracy === 100) {
    emoji = "🏆";
    title = "¡PERFECTO!";
    titleColor = "#FFD700";
  } else if (accuracy >= 80) {
    emoji = "🌟";
    title = "¡Excelente!";
    titleColor = "#34C759";
  } else if (accuracy >= 60) {
    emoji = "👍";
    title = "¡Bien hecho!";
    titleColor = "#FF9500";
  }

  return (
    <PageShell>
      {showConfetti && <Confetti pieces={100} />}

      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: "spring", bounce: 0.4 }}
        className="max-w-2xl mx-auto"
      >
        <div className="bg-white rounded-[2.5rem] border-8 border-white shadow-[0_25px_50px_-20px_rgba(0,0,0,0.15)] p-8 sm:p-12 text-center">
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", bounce: 0.6, delay: 0.2 }}
            className="text-7xl sm:text-8xl mb-3"
          >
            {emoji}
          </motion.div>
          <h1
            className="font-heading text-4xl sm:text-5xl font-bold mb-2"
            style={{ color: titleColor }}
            data-testid="results-title"
          >
            {title}
          </h1>
          {op && level && (
            <p className="text-base sm:text-lg text-[#5C5C60] mb-8">
              {op.name} · Nivel {level.name}
            </p>
          )}

          <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-8">
            <div
              className="bg-[#E8F9EE] rounded-2xl border-4 border-white p-4"
              data-testid="results-correct"
            >
              <p className="text-xs font-bold uppercase tracking-wider text-[#1A6B2F]">Aciertos</p>
              <p className="font-heading text-3xl sm:text-4xl font-bold text-[#1A6B2F]">
                {correct}/{total}
              </p>
            </div>
            <div
              className="bg-[#FFEAEA] rounded-2xl border-4 border-white p-4"
              data-testid="results-wrong"
            >
              <p className="text-xs font-bold uppercase tracking-wider text-[#803535]">Errores</p>
              <p className="font-heading text-3xl sm:text-4xl font-bold text-[#803535]">{wrong}</p>
            </div>
            <div
              className="bg-[#FFF6CC] rounded-2xl border-4 border-white p-4"
              data-testid="results-points"
            >
              <p className="text-xs font-bold uppercase tracking-wider text-[#7A5C00]">Puntos</p>
              <p className="font-heading text-3xl sm:text-4xl font-bold text-[#7A5C00]">
                +{lastRound.points}
              </p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-2xl border-2 border-gray-100 p-4 mb-8">
            <p className="text-sm font-bold uppercase tracking-wider text-[#5C5C60] mb-2">
              Precisión
            </p>
            <div className="h-4 rounded-full bg-white border-2 border-white shadow-inner overflow-hidden">
              <motion.div
                className="h-full rounded-full"
                style={{ backgroundColor: titleColor }}
                initial={{ width: 0 }}
                animate={{ width: `${accuracy}%` }}
                transition={{ duration: 0.8, delay: 0.5 }}
              />
            </div>
            <p className="mt-2 font-heading text-2xl font-bold" style={{ color: titleColor }}>
              {accuracy}%
            </p>
            {lastRound.maxStreak >= 3 && (
              <p className="mt-2 text-sm font-bold text-[#FF9500]">
                🔥 Mejor racha: {lastRound.maxStreak}
              </p>
            )}
          </div>

          {newlyUnlocked.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="bg-gradient-to-b from-[#FFF6CC] to-white rounded-2xl border-4 border-[#FFD700] p-5 mb-8"
              data-testid="results-medals"
            >
              <div className="flex items-center justify-center gap-2 mb-3">
                <Award className="w-6 h-6 text-[#FFD700]" strokeWidth={3} />
                <p className="font-heading text-xl font-bold text-[#7A5C00]">
                  ¡Nueva{newlyUnlocked.length > 1 ? "s" : ""} medalla
                  {newlyUnlocked.length > 1 ? "s" : ""}!
                </p>
              </div>
              <div className="flex items-center justify-center gap-4 flex-wrap">
                {newlyUnlocked.map((id) => {
                  const m = getMedal(id);
                  if (!m) return null;
                  return (
                    <motion.div
                      key={id}
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: "spring", bounce: 0.6, delay: 1 }}
                      className="text-center"
                    >
                      <img
                        src={MEDAL_IMAGES[m.tier]}
                        alt={m.name}
                        className="w-20 h-20 sm:w-24 sm:h-24 object-contain mx-auto"
                      />
                      <p className="mt-1 font-bold text-sm text-[#1C1C1E]">{m.name}</p>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <ClayButton
              variant="secondary"
              onClick={() => navigate("/dashboard")}
              data-testid="results-home-btn"
            >
              <HomeIcon className="w-5 h-5" strokeWidth={3} />
              Inicio
            </ClayButton>
            <ClayButton
              variant="secondary"
              onClick={() => navigate(buildLevelsPath(), { replace: true })}
              data-testid="results-levels-btn"
            >
              <ListChecks className="w-5 h-5" strokeWidth={3} />
              Cambiar nivel
            </ClayButton>
            <ClayButton
              onClick={() => navigate(buildReplayPath(), { replace: true })}
              data-testid="results-replay-btn"
            >
              <RotateCcw className="w-5 h-5" strokeWidth={3} />
              Jugar otra vez
            </ClayButton>
          </div>
        </div>
      </motion.div>
    </PageShell>
  );
}
